import React, { useState, useEffect } from 'react';
import { GCodePreviewCard, GCodeFile } from './GCodePreviewCard';
import { formatPrintTime, extractGCodeInfo } from '../utils/gcodeUtils';
import { api } from '../services/api';
import JSZip from 'jszip';
import './GCodeFolderCard.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isAnonymousUser } from '../utils/anonymousSession';
import { AnonymousLoginModal } from './AnonymousLoginModal';

export interface GCodeFolder {
  folder_name: string;
  readable_date: string;
  created_at: string | null;
  files: GCodeFile[];
  project_id?: string; // Optional project ID this folder belongs to
}

interface GCodeFolderCardProps {
  folder: GCodeFolder;
  onDelete?: (folder: GCodeFolder) => void; // Callback when folder is deleted
}

interface FolderStats {
  totalTime: number;
  totalFilamentWeight: number;
  loadedFilesCount: number;
  isLoading: boolean;
  printerInfo: string | null;
  nozzleDiameter: number | null;
  materialType: string | null;
}

export function GCodeFolderCard({ folder, onDelete }: GCodeFolderCardProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [folderStats, setFolderStats] = useState<FolderStats>({
    totalTime: 0,
    totalFilamentWeight: 0,
    loadedFilesCount: 0,
    isLoading: false,
    printerInfo: null,
    nozzleDiameter: null,
    materialType: null
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAnonymousModal, setShowAnonymousModal] = useState(false);

  // Toggle expand/collapse
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Download all GCode files in the folder as a zip
  const downloadAllFiles = async () => {
    // Check if user is anonymous - show login modal instead of downloading
    if (isAnonymousUser(currentUser)) {
      setShowAnonymousModal(true);
      return;
    }

    if (folder.files.length === 0) {
      console.warn(t('gcodeFolder.errors.noFilesToDownload'));
      return;
    }

    try {
      setIsDownloading(true);
      console.log(`[DEBUG] Starting download of ${folder.files.length} files from folder ${folder.folder_name}`);
      
      // Create a new zip file
      const zip = new JSZip();
      
      // Add each file to the zip
      const fetchPromises = folder.files.map(async (file) => {
        try {
          // Fetch the file content
          const response = await fetch(file.download_url);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${file.filename}: ${response.statusText}`);
          }
          
          // Get file content as text
          const content = await response.text();
          
          // Add file to zip
          zip.file(file.filename, content);
          console.log(`[DEBUG] Added ${file.filename} to zip`);
          
          return true;
        } catch (error) {
          console.error(`[DEBUG] Error adding ${file.filename} to zip:`, error);
          return false;
        }
      });
      
      // Wait for all fetch operations to complete
      await Promise.all(fetchPromises);
      
      // Generate the zip file
      console.log("[DEBUG] Generating zip file...");
      const zipBlob = await zip.generateAsync({ 
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      });
      
      // Create download link for the zip
      const zipUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = zipUrl;
      
      // Set a meaningful filename based on folder name
      const zipFilename = `gcode_${folder.folder_name}.zip`;
      link.download = zipFilename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL object
      URL.revokeObjectURL(zipUrl);
      
      console.log(`[DEBUG] Successfully downloaded zip file: ${zipFilename}`);
    } catch (error) {
      console.error("[DEBUG] Error creating zip file:", error);
      alert(t('gcodeFolder.errors.zipCreationFailed'));
    } finally {
      setIsDownloading(false);
    }
  };

  // Format filament weight with proper units
  const formatFilamentWeight = (weight: number): string => {
    if (weight === null || weight === undefined || weight < 0) return 'N/A';
    if (weight === 0) return '0g';
    if (weight < 1) {
      return `${(weight * 1000).toFixed(0)}mg`; // Convert small amounts to mg
    }
    return `${weight.toFixed(1)}g`;
  };

  // Load content and extract stats for all GCode files in the folder
  useEffect(() => {
    const loadFolderStats = async () => {
      // Only load stats when there are files and they haven't been loaded yet
      if (folder.files.length === 0 || folderStats.isLoading || 
          (folderStats.loadedFilesCount > 0 && folderStats.loadedFilesCount === folder.files.length)) {
        return;
      }

      setFolderStats(prev => ({ ...prev, isLoading: true }));
      
      let totalTime = 0;
      let totalFilamentWeight = 0;
      let loadedCount = 0;
      let printerInfo: string | null = null;
      let nozzleDiameter: number | null = null;
      let materialType: string | null = null;

      for (const file of folder.files) {
        try {
          // Fetch GCode content
          const response = await fetch(file.download_url);
          if (!response.ok) {
            console.error(`[DEBUG] Failed to fetch G-code file: ${file.filename}`);
            continue;
          }
          
          const gcodeContent = await response.text();
          
          // Extract GCode info
          const info = extractGCodeInfo(gcodeContent);
          
          // Add to totals
          if (info.estimatedTime) {
            totalTime += info.estimatedTime;
          }
          
          if (info.filamentWeight) {
            totalFilamentWeight += info.filamentWeight;
          }
          
          // Capture printer info from the first file that has it
          if (!printerInfo && info.printerInfo) {
            printerInfo = info.printerInfo;
          }
          
          // Capture nozzle diameter from the first file that has it
          if (!nozzleDiameter && info.nozzleDiameter) {
            nozzleDiameter = info.nozzleDiameter;
          }
          
          // Capture material type from the first file that has it
          if (!materialType && info.materialType) {
            materialType = info.materialType;
          }
          
          loadedCount++;
        } catch (err) {
          console.error(`[DEBUG] Error loading G-code file ${file.filename}:`, err);
        }
      }

      // Update stats
      setFolderStats({
        totalTime,
        totalFilamentWeight,
        loadedFilesCount: loadedCount,
        isLoading: false,
        printerInfo,
        nozzleDiameter,
        materialType
      });
    };

    loadFolderStats();
  }, [folder.files]);

  // Delete the GCode folder and all its files
  const deleteFolder = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isDeleting) return;
    
    if (!folder.project_id) {
      console.error('Cannot delete folder: missing project_id');
      alert(t('gcodeFolder.errors.cannotDelete'));
      return;
    }
    
    // Confirm deletion
    if (!window.confirm(t('gcodeFolder.confirm.deleteFolder'))) {
      return;
    }
    
    try {
      setIsDeleting(true);
      
      const result = await api.gcode.deleteGCodeFolder(folder.project_id, folder.folder_name);
      
      if (result.success) {
        console.log(`[DEBUG] Successfully deleted G-code folder: ${folder.folder_name}`);
        // Call the onDelete callback if provided
        if (onDelete) {
          onDelete(folder);
        }
      } else {
        console.error(`[DEBUG] Failed to delete G-code folder: ${result.error}`);
        alert(`${t('gcodeFolder.errors.deletionError')}: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('[DEBUG] Error deleting G-code folder:', error);
      alert(t('gcodeFolder.errors.deletionError'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="gcode-folder-card">
      <div className="gcode-folder-header" onClick={toggleExpand}>
        <div className="folder-icon">
        </div>
        <div className="folder-info">
          <div className="folder-header-row">
            <h3>{folder.readable_date}</h3>
          </div>
          <div className="folder-metadata">
            {folderStats.isLoading ? (
              <p className="folder-loading">{t('gcodeFolder.loading')}</p>
            ) : folderStats.loadedFilesCount > 0 ? (
              <>
                <div className="folder-stats-info">
                  <span className="files-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  </span>
                  {folder.files.length} {folder.files.length !== 1 ? t('gcodeFolder.metadata.files') : t('gcodeFolder.metadata.file')}
                  {folderStats.printerInfo && (
                    <>
                      <span className="metadata-separator">|</span>
                      <span className="printer-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="6" y="4" width="12" height="6"></rect>
                          <line x1="6" y1="10" x2="18" y2="10"></line>
                          <line x1="9" y1="14" x2="15" y2="14"></line>
                          <line x1="12" y1="10" x2="12" y2="18"></line>
                        </svg>
                      </span>
                      <span className="printer-name">{folderStats.printerInfo}</span>
                    </>
                  )}
                </div>
                <span className="metadata-separator">|</span>
                <div className="folder-metadata-item">
                  <span className="time-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </span>
                  <span className="metadata-label">{t('gcodeFolder.metadata.totalTime')}</span> 
                  <span className="metadata-value">{folderStats.totalTime > 0 ? formatPrintTime(folderStats.totalTime) : 'N/A'}</span>
                </div>
                <span className="metadata-separator">|</span>
                <div className="folder-metadata-item">
                  <span className="filament-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                      <path d="M2 17l10 5 10-5"></path>
                      <path d="M2 12l10 5 10-5"></path>
                    </svg>
                  </span>
                  <span className="metadata-label">{t('gcodeFolder.metadata.totalFilament')}</span> 
                  <span className="metadata-value">{formatFilamentWeight(folderStats.totalFilamentWeight)}</span>
                </div>
                {folderStats.nozzleDiameter && (
                  <>
                    <span className="metadata-separator">|</span>
                    <div className="folder-metadata-item">
                      <span className="nozzle-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1.05 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1.05H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1.05-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1.05 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1.05H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1.05z"></path>
                        </svg>
                      </span>
                      <span className="metadata-label">{t('gcodeFolder.metadata.nozzle')}</span> 
                      <span className="metadata-value">{folderStats.nozzleDiameter}mm</span>
                    </div>
                  </>
                )}
                {folderStats.materialType && (
                  <>
                    <span className="metadata-separator">|</span>
                    <div className="folder-metadata-item">
                      <span className="material-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      </span>
                      <span className="metadata-label">{t('gcodeFolder.metadata.material')}</span> 
                      <span className="metadata-value">{folderStats.materialType}</span>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        </div>
        <div className="folder-controls">
          <button 
            className={`delete-button ${isDeleting ? 'loading' : ''}`}
            onClick={deleteFolder}
            disabled={isDeleting}
            title={t('gcodeFolder.actions.deleteCollection')}
          >
            {isDeleting ? (
              <span className="button-spinner"></span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '64px', height: '64px'}}>
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            )}
          </button>
          <button 
            className={`download-all-button ${isDownloading ? 'loading' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDownloading) {
                downloadAllFiles();
              }
            }}
            disabled={isDownloading}
            title={t('gcodeFolder.actions.downloadAll')}
          >
            {isDownloading ? (
              <span className="button-spinner"></span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '64px', height: '64px'}}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            )}
          </button>
          <button 
            className="expand-button"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}
            title={isExpanded ? t('gcodeFolder.actions.collapseFolder') : t('gcodeFolder.actions.expandFolder')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '64px', height: '64px'}}>
              <polyline points={isExpanded ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
            </svg>
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="folder-content">
          <div className="gcode-grid">
            {folder.files.map((file) => (
              <GCodePreviewCard
                key={`${folder.folder_name}-${file.filename}`}
                file={file}
              />
            ))}
          </div>
        </div>
      )}

      {/* Anonymous Login Modal */}
      <AnonymousLoginModal
        isOpen={showAnonymousModal}
        onClose={() => setShowAnonymousModal(false)}
        downloadType="gcode"
        fileName={`${folder.folder_name}.zip`}
      />
    </div>
  );
}